import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DollarSign, Search, Calendar, FileText, User as UserIcon, Wallet, Package, Paperclip, ExternalLink, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);
const fmtDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return d; }
};

const tipoLabel = { pagamento_manual: 'Manual', boleto: 'Boleto', avista: 'À Vista' };
const tipoCor = { pagamento_manual: 'bg-blue-100 text-blue-700', boleto: 'bg-purple-100 text-purple-700', avista: 'bg-emerald-100 text-emerald-700' };

/**
 * PagamentosRegistrados — página admin com consolidado de TODOS os pagamentos.
 * Consome GET /api/admin/pagamentos-registrados.
 * Cada linha mostra: quando, quanto, como, quem, por qual romaneio, e comprovantes.
 */
export default function PagamentosRegistrados() {
  const [data, setData] = useState({ registros: [], total_registros: 0, total_valor: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tipoFilter, setTipoFilter] = useState('');
  const [formaFilter, setFormaFilter] = useState('');

  const carregar = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/pagamentos-registrados`);
      setData(r.data || { registros: [] });
    } catch (e) {
      toast.error('Erro ao carregar pagamentos: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregar(); }, []);

  const filtered = useMemo(() => {
    return (data.registros || []).filter(r => {
      if (tipoFilter && r.tipo !== tipoFilter) return false;
      if (formaFilter && (r.forma_pagamento || '').toLowerCase() !== formaFilter.toLowerCase()) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = `${r.numero_pedido} ${r.cliente_nome} ${r.vendedor_nome} ${r.registrado_por} ${r.observacao || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data.registros, search, tipoFilter, formaFilter]);

  const totalFiltrado = useMemo(() =>
    filtered.reduce((s, r) => s + (Number(r.valor) || 0), 0)
  , [filtered]);

  const formasUnicas = useMemo(() => {
    const set = new Set();
    (data.registros || []).forEach(r => r.forma_pagamento && set.add(r.forma_pagamento));
    return Array.from(set).sort();
  }, [data.registros]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-emerald-600" />
            Registro de Pagamentos
          </h1>
          <p className="text-sm text-muted-foreground">Auditoria completa: todo pagamento já registrado, com data, forma, comprovante e quem lançou.</p>
        </div>
        <button
          onClick={carregar}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-border hover:bg-accent text-sm"
          data-testid="pagamentos-refresh"
        >
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="p-4 rounded-lg border border-border bg-card">
          <p className="text-xs uppercase text-muted-foreground font-semibold">Total de Registros</p>
          <p className="text-2xl font-bold" data-testid="kpi-total-registros">{data.total_registros}</p>
        </div>
        <div className="p-4 rounded-lg border border-border bg-card">
          <p className="text-xs uppercase text-muted-foreground font-semibold">Total Recebido</p>
          <p className="text-2xl font-bold text-emerald-600" data-testid="kpi-total-valor">{fmtBRL(data.total_valor)}</p>
        </div>
        <div className="p-4 rounded-lg border border-border bg-card">
          <p className="text-xs uppercase text-muted-foreground font-semibold">Filtrado</p>
          <p className="text-2xl font-bold text-blue-600" data-testid="kpi-filtrado">
            {filtered.length} · {fmtBRL(totalFiltrado)}
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="p-3 rounded-lg border border-border bg-card flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por romaneio, cliente, vendedor…"
            className="w-full pl-8 pr-3 py-1.5 rounded-md border border-border bg-background text-sm"
            data-testid="pagamentos-search"
          />
        </div>
        <select
          value={tipoFilter}
          onChange={(e) => setTipoFilter(e.target.value)}
          className="px-2 py-1.5 rounded-md border border-border bg-background text-sm"
          data-testid="pagamentos-filter-tipo"
        >
          <option value="">Todos os tipos</option>
          <option value="pagamento_manual">Manual</option>
          <option value="boleto">Boleto</option>
          <option value="avista">À Vista</option>
        </select>
        <select
          value={formaFilter}
          onChange={(e) => setFormaFilter(e.target.value)}
          className="px-2 py-1.5 rounded-md border border-border bg-background text-sm"
          data-testid="pagamentos-filter-forma"
        >
          <option value="">Todas as formas</option>
          {formasUnicas.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="text-center py-10 text-muted-foreground italic">Carregando…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-10 border-2 border-dashed border-border rounded-lg text-muted-foreground">
          Nenhum pagamento encontrado com esses filtros.
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="max-h-[600px] overflow-y-auto divide-y divide-border" data-testid="pagamentos-list">
            {filtered.map((r, idx) => (
              <div key={`${r.romaneio_id}-${r.tipo}-${r.index ?? r.boleto_id ?? idx}`} className="p-3 hover:bg-accent/30" data-testid={`pagamento-row-${idx}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${tipoCor[r.tipo] || 'bg-gray-100'}`}>
                        {tipoLabel[r.tipo] || r.tipo}
                      </span>
                      <span className="font-semibold text-sm">Pedido #{r.numero_pedido}</span>
                      {r.boleto_numero && <span className="text-xs text-muted-foreground">Parcela {r.boleto_numero}</span>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span className="inline-flex items-center gap-1"><UserIcon className="w-3 h-3" /> {r.cliente_nome || '-'}</span>
                      {r.vendedor_nome && <span>Vend: {r.vendedor_nome}</span>}
                      <span className="inline-flex items-center gap-1"><Wallet className="w-3 h-3" /> {r.forma_pagamento || '-'}</span>
                      <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmtDate(r.data)}</span>
                      {r.registrado_por && <span>Lançado por: <b>{r.registrado_por}</b></span>}
                      {r.registrado_em && r.registrado_em !== r.data && (
                        <span className="text-[10px] italic">reg. em {fmtDate(r.registrado_em)}</span>
                      )}
                    </div>
                    {r.observacao && (
                      <p className="text-xs mt-1 italic text-muted-foreground">&ldquo;{r.observacao}&rdquo;</p>
                    )}
                    {r.comprovantes && r.comprovantes.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {r.comprovantes.map((c, ci) => (
                          <a
                            key={ci}
                            href={c.url || c.arquivo_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 text-[11px] font-semibold hover:bg-blue-100"
                            data-testid={`pagamento-comprovante-${idx}-${ci}`}
                          >
                            <Paperclip className="w-3 h-3" />
                            Comprovante {ci + 1}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-lg font-bold text-emerald-600 tabular-nums" data-testid={`pagamento-valor-${idx}`}>
                      {fmtBRL(r.valor)}
                    </p>
                    <p className="text-[10px] text-muted-foreground">de {fmtBRL(r.valor_total)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-muted-foreground italic text-center">
        Inclui: pagamentos manuais registrados no admin, boletos marcados como pagos, e romaneios &ldquo;à vista&rdquo; quitados na criação.
      </p>
    </div>
  );
}
